import { useState, useEffect } from 'react';
import { FormField, Input, Select, Checkbox, Button, Modal } from './ui';

interface User {
  userId: string;
  email: string;
  subscription: {
    tier: 'trial' | 'pro';
    status: 'active' | 'cancelled' | 'expired';
    aiLimit: number;
    overageEnabled: boolean;
    expirationDate?: string | null;
    stripeSubscriptionId?: string | null;
  };
}

interface SubscriptionEditorProps {
  user: User;
  onUpdate: (updates: any) => Promise<void>;
  onGrantFreePro: (expirationDate?: string | null) => Promise<void>;
  loading: boolean;
}

function formatExpirationDate(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

const tierOptions = [
  { value: 'trial', label: 'Trial' },
  { value: 'pro', label: 'Pro' },
];

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'expired', label: 'Expired' },
];

export default function SubscriptionEditor({
  user,
  onUpdate,
  onGrantFreePro,
  loading,
}: SubscriptionEditorProps) {
  const [tier, setTier] = useState<'trial' | 'pro'>(user.subscription?.tier || 'trial');
  const [status, setStatus] = useState<'active' | 'cancelled' | 'expired'>(user.subscription?.status || 'active');
  const [aiLimit, setAiLimit] = useState<number>(user.subscription?.aiLimit || 100);
  const [overageEnabled, setOverageEnabled] = useState<boolean>(user.subscription?.overageEnabled || false);
  const [giftExpirationDate, setGiftExpirationDate] = useState('');
  const [noExpiration, setNoExpiration] = useState(true);
  const [grantModalOpen, setGrantModalOpen] = useState(false);
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);

  useEffect(() => {
    setTier(user.subscription?.tier || 'trial');
    setStatus(user.subscription?.status || 'active');
    setAiLimit(user.subscription?.aiLimit ?? 100);
    setOverageEnabled(user.subscription?.overageEnabled ?? false);
  }, [
    user.subscription?.tier,
    user.subscription?.status,
    user.subscription?.aiLimit,
    user.subscription?.overageEnabled,
  ]);

  const hasStripeSub = Boolean(user.subscription?.stripeSubscriptionId);
  const currentExpiration = user.subscription?.expirationDate;

  const openGrantModal = () => setGrantModalOpen(true);

  const handleGrantConfirm = async () => {
    const expiration =
      noExpiration ? null : (giftExpirationDate ? new Date(giftExpirationDate).toISOString() : undefined);
    await onGrantFreePro(expiration);
  };

  const handleSave = async () => {
    await onUpdate({
      subscription: {
        tier,
        status,
        aiLimit,
        overageEnabled,
      },
    });
  };

  const openRevokeModal = () => setRevokeModalOpen(true);

  const handleRevokeConfirm = async () => {
    await onUpdate({
      subscription: {
        tier: 'trial',
        status: 'active',
        expirationDate: null,
      },
    });
  };

  const handleClearExpiration = async () => {
    await onUpdate({ subscription: { expirationDate: null } });
  };

  return (
    <div className="space-y-8">
      {/* Gift Pro section */}
      <section className="rounded-2xl border border-neutral-border bg-white p-6 shadow-card">
        <h3 className="text-base font-semibold text-neutral-primary mb-4">Gift Pro</h3>
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <FormField label="Expires at (optional)" className="min-w-[160px]">
              <Input
                type="date"
                value={giftExpirationDate}
                onChange={(e) => {
                  setGiftExpirationDate(e.target.value);
                  if (e.target.value) setNoExpiration(false);
                }}
                disabled={hasStripeSub}
              />
            </FormField>
            <div className="pb-1">
              <Checkbox
                label="No expiration"
                checked={noExpiration}
                onChange={(e) => {
                  setNoExpiration(e.target.checked);
                  if (e.target.checked) setGiftExpirationDate('');
                }}
                disabled={hasStripeSub}
              />
            </div>
            <Button
              variant="primary"
              onClick={openGrantModal}
              disabled={loading || hasStripeSub}
              className="bg-green-600 hover:bg-green-700 focus-visible:ring-green-500/20"
            >
              {loading ? 'Granting...' : 'Grant Free Pro'}
            </Button>
          </div>
          {hasStripeSub && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              User has an active subscription; cancel it first.
            </p>
          )}
          {user.subscription?.tier === 'pro' && currentExpiration && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-neutral-secondary">Gift expires:</span>
              <span className="text-neutral-primary font-medium">{formatExpirationDate(currentExpiration)}</span>
              <Button variant="ghost" onClick={handleClearExpiration} disabled={loading} className="h-auto py-1 px-2 text-brand-primary hover:bg-brand-primary/10">
                Clear expiration
              </Button>
            </div>
          )}
          {user.subscription?.tier === 'pro' && (
            <Button variant="danger" onClick={openRevokeModal} disabled={loading || hasStripeSub}>
              Revoke Pro
            </Button>
          )}
        </div>
      </section>

      {/* Subscription section */}
      <section className="rounded-2xl border border-neutral-border bg-white p-6 shadow-card">
        <div className="mb-4 flex items-center justify-between gap-2">
          <h3 className="text-base font-semibold text-neutral-primary">Subscription</h3>
          {user.subscription?.tier === 'pro' && (
            <span
              className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${
                hasStripeSub ? 'bg-blue-100 text-blue-800' : 'bg-violet-100 text-violet-800'
              }`}
              title={hasStripeSub ? 'Pro via paid subscription' : 'Pro granted as gift (admin)'}
            >
              {hasStripeSub ? 'Pro · Subscription' : 'Pro · Gift'}
            </span>
          )}
        </div>
        <div className="space-y-4 max-w-sm">
          <FormField label="Tier">
            <Select
              options={tierOptions}
              value={tier}
              onChange={(e) => setTier(e.target.value as 'trial' | 'pro')}
            />
          </FormField>
          <FormField label="Status">
            <Select
              options={statusOptions}
              value={status}
              onChange={(e) => setStatus(e.target.value as 'active' | 'cancelled' | 'expired')}
            />
          </FormField>
          <FormField label="AI Limit">
            <Input
              type="number"
              value={aiLimit}
              onChange={(e) => setAiLimit(parseInt(e.target.value) || 0)}
              min={0}
            />
          </FormField>
          <div className="pt-1">
            <Checkbox
              label="Overage enabled"
              checked={overageEnabled}
              onChange={(e) => setOverageEnabled(e.target.checked)}
            />
          </div>
          <Button variant="primary" onClick={handleSave} disabled={loading} className="w-full">
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </section>
      <Modal
        open={grantModalOpen}
        onClose={() => setGrantModalOpen(false)}
        message="Grant free pro to this user?"
        variant="confirm"
        confirmLabel="Grant"
        onConfirm={handleGrantConfirm}
      />
      <Modal
        open={revokeModalOpen}
        onClose={() => setRevokeModalOpen(false)}
        message="Revoke pro subscription and set to trial?"
        variant="confirm"
        confirmLabel="Revoke"
        confirmVariant="danger"
        onConfirm={handleRevokeConfirm}
      />
    </div>
  );
}
